import { getBusinessTheme } from "./businessThemes";

const hexWithAlpha = (hex: string, alpha: number): string => {
  const normalized = hex.replace("#", "").trim();
  if (!/^[0-9a-fA-F]{6}$/.test(normalized)) {
    return "";
  }
  const r = parseInt(normalized.slice(0, 2), 16);
  const g = parseInt(normalized.slice(2, 4), 16);
  const b = parseInt(normalized.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const CSS_VARS = [
  "nav-bg",
  "nav-text",
  "nav-border",
  "dropdown-bg",
  "dropdown-item",
  "dropdown-hover-bg",
  "dropdown-hover-text",
  "icon",
  "icon-hover",
  "tooltip-bg",
  "button-primary",
  "page-bg",
  "card-bg",
  "welcome-bg",
  "welcome-title",
  "welcome-subtitle",
  "card-text",
  "active-nav-bg",
  "active-nav-text",
  "profile-btn-bg",
  "profile-btn-text",
  "hero-bg",
  "hero-border",
  "hero-title",
  "hero-subtitle",
  "hero-icon-bg",
  "hero-icon-color",
  "table-head-bg",
  "table-head-text",
  "table-head-border",
  "table-row-hover",
] as const;

/** Aplica variables CSS globales según el grupo de negocio activo. */
export const applyBusinessTheme = (rawGroupId: number | string | null | undefined) => {
  const theme = getBusinessTheme(rawGroupId);
  const groupId = Number(rawGroupId) || 1;
  const root = document.documentElement;

  const vars: Record<string, string> = {
    "nav-bg": theme.navBackground,
    "nav-text": theme.navTextColor,
    "nav-border": theme.navBorder,
    "dropdown-bg": theme.dropdownBg,
    "dropdown-item": theme.dropdownItemColor,
    "dropdown-hover-bg": theme.dropdownHoverBg,
    "dropdown-hover-text": theme.dropdownHoverText,
    "icon": theme.iconColor,
    "icon-hover": theme.iconHoverColor,
    "tooltip-bg": theme.tooltipBackground,
    "button-primary": theme.buttonPrimary,
    "page-bg": theme.navSolidColor,
    "card-bg": theme.cardBackground,
    "welcome-bg": theme.welcomeSectionBg,
    "welcome-title": theme.welcomeTitleColor,
    "welcome-subtitle": theme.welcomeSubtitleColor,
    "card-text": theme.cardTextColor,
    "active-nav-bg": theme.activeNavBg,
    "active-nav-text": theme.activeNavText,
    "profile-btn-bg": theme.profileBtnBg,
    "profile-btn-text": theme.profileBtnText,
    "hero-bg": "#ffffff",
    "hero-border": hexWithAlpha(theme.navBorder, 0.35) || "#e8ecf0",
    "hero-title": theme.cardTextColor,
    "hero-subtitle": theme.welcomeSubtitleColor === "#FFFFFF"
      ? "#5a6a7a"
      : theme.welcomeSubtitleColor,
    "hero-icon-bg": hexWithAlpha(theme.buttonPrimary, 0.14),
    "hero-icon-color": theme.buttonPrimary,
    "table-head-bg": "#ffffff",
    "table-head-text": theme.cardTextColor,
    "table-head-border": hexWithAlpha(theme.navBorder, 0.25) || "#e8ecf0",
    "table-row-hover": hexWithAlpha(theme.buttonPrimary, 0.08) || "#f8fafb",
  };

  CSS_VARS.forEach((key) => {
    root.style.setProperty(`--theme-${key}`, vars[key]);
  });

  root.style.setProperty("--theme-hero-stat-pending-bg", hexWithAlpha(theme.buttonPrimary, 0.18));
  root.style.setProperty("--theme-hero-stat-pending-text", theme.cardTextColor);
  root.style.setProperty("--theme-hero-stat-done-bg", "#eef1f4");
  root.style.setProperty("--theme-hero-stat-done-text", theme.cardTextColor);
  root.style.setProperty("--theme-hero-stat-total-text", theme.isDarkLayout ? theme.profileBtnText : "#ffffff");
  root.style.setProperty(
    "--theme-table-head-shadow",
    `0 2px 10px ${hexWithAlpha(theme.buttonPrimary, 0.14) || "rgba(155, 221, 51, 0.12)"}`
  );
  root.style.setProperty(
    "--theme-table-head-accent",
    hexWithAlpha(theme.buttonPrimary, 0.28) || "rgba(155, 221, 51, 0.25)"
  );

  root.style.setProperty("--theme-nav-radius", "14px");
  document.body.style.background = theme.navSolidColor;
  document.body.style.minHeight = "100vh";

  // Compatibilidad con estilos globales existentes en App.css
  root.style.setProperty("--primary-color", theme.buttonPrimary);
  root.style.setProperty("--secondary-color", theme.navSolidColor);
  root.style.setProperty("--secondary-light", theme.navSolidColor);

  document.body.dataset.businessGroup = String(groupId);
  if (theme.isDarkLayout) {
    document.body.dataset.client = "digital";
  } else {
    delete document.body.dataset.client;
  }
};

export const clearBusinessTheme = () => {
  const root = document.documentElement;
  CSS_VARS.forEach((key) => root.style.removeProperty(`--theme-${key}`));
  [
    "hero-stat-pending-bg",
    "hero-stat-pending-text",
    "hero-stat-done-bg",
    "hero-stat-done-text",
    "hero-stat-total-text",
    "table-head-shadow",
    "table-head-accent",
  ].forEach((key) => root.style.removeProperty(`--theme-${key}`));
  root.style.removeProperty("--primary-color");
  root.style.removeProperty("--secondary-color");
  root.style.removeProperty("--secondary-light");
  delete document.body.dataset.businessGroup;
  delete document.body.dataset.client;
  document.body.style.background = "";
  document.body.style.minHeight = "";
};
